import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatIcon } from "@angular/material/icon";
import { MatSliderModule } from '@angular/material/slider';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { TimelineSegment, Motion, TrimSelection } from '../../interfaces/timeline-segments';
import { TimelineEngine, TimelineTick } from '../../services/timeline-engine';
import { Camera } from '../../interfaces/camera';
import { Stage } from '../stage/stage';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Subject } from 'rxjs';



type TimelineEvent = {
  type: string;
  cameraId: number;
  start: Date;
  end: Date;
  segment: TimelineSegment;
};



@Component({
  standalone:true,
  selector: 'lib-player',
  imports: [MatIcon, MatSliderModule, CommonModule, FormsModule,MatFormFieldModule,MatSelectModule,MatFormFieldModule, MatSlideToggleModule],
  templateUrl: './player.html',
  styleUrl: './player.scss',
})
export class Player implements OnInit, OnChanges, OnDestroy {
  /**
   * Ensure that subscriptions are destroyed at EOL
   */
  private destroy$ = new Subject<void>();

  selectedEndDateString = '';

  @Input() selectedCameraList!: Camera[];
  @Input() maximumViewPortWidth:string = '95vw';
  @Input() stage!: Stage;
  
  speeds=[1,1.5,2,4,8, 16];
  selectedSpeed = 1;
  pixelsPerMinute = 20;
  scaleTicks: TimelineTick[] = [];
  currentTickerTime!: Date;
  /** IANA zone for the timeline clock. Empty keeps the browser-local date pipe. */
  tickerTimeZone = '';
  /**
   * When set, rebuilding the timeline keeps this instant instead of reading
   * the previous scroll position. Cleared when the user drags, zooms, or seeks.
   */
  pinnedPlayhead: Date | null = null;
  trimMode:boolean = false;
  activeTrimSelection!:TrimSelection | null;

  private forceSeekNextUpdate:boolean = false;
  activeEventType: string | null = null;

  allEvents: TimelineEvent[] = [];

  cameraColumnWidth:number = 0;

  isPlaying:boolean = false;

  @ViewChild('timelineContainerRef')timelineContainerRef!: ElementRef<HTMLDivElement>;


  @Input() motionOptions!:Motion[];
  selectedMotions: string[] = [];
  engine:TimelineEngine = new TimelineEngine();
  @Input() startDate: Date;
  @Input() endDate: Date;

  isDragging:boolean = false;
  startX:number = 0;
  startScrollLeft:number = 0;
  viewportSpacer:number = 0;

  @Input() videoTime: Date | null = null;



  @ViewChild('timelineScroller')
  timelineScroller!: ElementRef<HTMLDivElement>;

  @Output() timelineChanged = new EventEmitter<{
      start: Date;
      end: Date;
    }>();

  @Output() onDownloadSegment: EventEmitter<TrimSelection> = new EventEmitter();

  splitEventLanes = false;

  constructor(){


  this.endDate = new Date();

  this.endDate.setMinutes(0, 0, 0);

  this.startDate = new Date(
    this.endDate.getTime() -
    24 * 60 * 60 * 1000
  );

  this.selectedEndDateString =
    this.formatDateForInput(
      this.endDate
    );         
  }

private rebuildTimeline() {

  this.engine.init({
    startDate: this.startDate,
    endDate: this.endDate,
    pixelsPerMinute: this.pixelsPerMinute,
    timeZone: this.tickerTimeZone.trim() || undefined,
  });


  this.scaleTicks =
    this.engine.generateTicks();

  requestAnimationFrame(() => {

    const container =
      this.timelineContainerRef.nativeElement;

    this.viewportSpacer =
      container.clientWidth / 2
      - this.engine.padding;

    this.updateTickerTime();

  });
  this.rebuildEventIndex();

}


ngOnChanges(changes: SimpleChanges) {

  if (
    changes['startDate'] ||
    changes['endDate']
  ) {
    this.rebuildTimeline();
  }

  if (
    changes['videoTime'] &&
    this.videoTime &&
    this.isPlaying
  ) {

    this.currentTickerTime =
      this.videoTime;

  }

}

ngOnInit() {
  this.rebuildTimeline();
  document.addEventListener(
        'mousemove',
        this.onTrimDragMove
    );

    document.addEventListener(
        'mouseup',
        this.onTrimDragEnd
    );
}

  ngOnDestroy() {
      document.removeEventListener(
    'mousemove',
    this.onTrimDragMove
  );

  document.removeEventListener(
    'mouseup',
    this.onTrimDragEnd
  );
    this.destroy$.next();
    this.destroy$.complete();
  }

  formatTickerTime(date: Date | null | undefined): string {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      return '';
    }
    const timeZone = this.tickerTimeZone.trim();
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || undefined,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);
    const get = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((part) => part.type === type)?.value ?? '';
    const hour = get('hour') === '24' ? '00' : get('hour');
    return `${get('year')}-${get('month')}-${get('day')} ${hour}:${get('minute')}:${get('second')}`;
  }

  private formatDateForInput(date: Date): string {

  const pad = (n: number) =>
    n.toString().padStart(2, '0');

  return `${date.getFullYear()}-${
    pad(date.getMonth() + 1)
  }-${
    pad(date.getDate())
  }T${
    pad(date.getHours())
  }:${
    pad(date.getMinutes())
  }`;

}

onEndDateChange(value: string) {
  const end =
    new Date(value);

  if (isNaN(end.getTime())) {
    return;
  }

  /**
   * normalize seconds
   */
  end.setSeconds(0, 0);

  /**
   * recalculate start
   * based on slider
   */
  const start =
    new Date(
      end.getTime() -
      this.sliderValue * 3600000
    );

  this.endDate = end;
  this.selectedEndDateString =
  this.formatDateForInput(
    this.endDate
  );
  this.startDate = start;

  /**
   * rebuild timeline
   */
  this.rebuildTimeline();

  /**
   * notify parent
   * fetch segments again
   */
  this.timelineChanged.emit({
    start,
    end
  });

  /**
   * sync ticker
   */
  setTimeout(() => {
    this.updateTickerTime();
  });

}



  max = 72;
  min = 24;
  showTicks = true;
  step = 12;
  sliderValue = 24;




onRangeChange(hours: number) {
  const end = new Date(this.endDate);

  end.setSeconds(0, 0);

  const start = new Date(
    end.getTime() - hours * 3600000
  );

  // update local timeline state
  this.startDate = start;
  this.endDate = end;

  this.selectedEndDateString =
  this.formatDateForInput(
    this.endDate
  );

  // rebuild scale/world immediately
  this.rebuildTimeline();

  // notify parent to fetch segments
  this.timelineChanged.emit({
    start,
    end
  });

  // keep ticker synced
  setTimeout(() => {
    this.updateTickerTime();
  });
}

getSegmentLaneStyle(
  seg: TimelineSegment,
  motionType: string,
  camera: Camera
) {

  /**
   * split disabled
   */
  if (!this.splitEventLanes) {

    return {
      top: '0%',
      height: '100%'
    };

  }

  /**
   * find overlapping event types
   */
  const overlappingTypes =
    this.selectedMotionOptions.filter(type => {

      const segments =
        camera?.segments?.[type.value] ?? [];

      return segments.some(other => {

        return (
          new Date(seg.start) <
            new Date(other.end) &&

          new Date(seg.end) >
            new Date(other.start)
        );

      });

    });

  /**
   * only one overlap
   * keep full height
   */
  if (overlappingTypes.length <= 1) {

    return {
      top: '0%',
      height: '100%'
    };

  }

  const index =
    overlappingTypes.findIndex(
      t => t.value === motionType
    );

  const laneHeight =
    100 / overlappingTypes.length;

  return {

    top: `${index * laneHeight}%`,
    height: `${laneHeight}%`

  };

}


getCombinedSegmentStyles(
  seg: TimelineSegment,
  color: string,
  motionType: string,
  camera: Camera
) {

  return {

    ...this.getSegmentStyles(
      seg,
      color
    ),

    ...this.getSegmentLaneStyle(
      seg,
      motionType,
      camera
    )

  };

}


getTrimSelectionStyles() {

  if (!this.activeTrimSelection) {
    return {};
  }

  const laneStyles =
    this.getSegmentLaneStyle(
      this.activeTrimSelection.segment,
      this.activeTrimSelection.eventType,
      this.activeTrimSelection.camera
    );

  return {

    left: `${
      this.engine.getPixel(
        this.activeTrimSelection.trimStart
      )
    }px`,

    width: `${
      this.engine.getPixel(
        this.activeTrimSelection.trimEnd
      ) -

      this.engine.getPixel(
        this.activeTrimSelection.trimStart
      )
    }px`,

    ...laneStyles

  };

}

getTrimHandleStyles(
  side: 'left' | 'right'
) {

  if (!this.activeTrimSelection) {
    return {};
  }

  const laneStyles =
    this.getSegmentLaneStyle(
      this.activeTrimSelection.segment,
      this.activeTrimSelection.eventType,
      this.activeTrimSelection.camera
    );

  return {

    left: `${
      this.engine.getPixel(
        side === 'left'
          ? this.activeTrimSelection.trimStart
          : this.activeTrimSelection.trimEnd
      )
    }px`,

    ...laneStyles

  };

}

trackBySegment(index: number, seg: TimelineSegment) {
  return seg.start.getTime();
}
trackByIndex(index: number): number {
  return index;
}

onDragStart(event: MouseEvent) {
  this.pinnedPlayhead = null;
  this.isDragging = true;
  this.onPause();

  this.startX = event.clientX;

  this.startScrollLeft =
    this.timelineScroller.nativeElement.scrollLeft;
}

onDragMove(event: MouseEvent) {
  if (!this.isDragging) return;

  const delta = event.clientX - this.startX;

  this.timelineScroller.nativeElement.scrollLeft =
    this.startScrollLeft - delta;

  const scrollLeft =
    this.timelineScroller.nativeElement.scrollLeft;

  if (!this.isPlaying) {
    this.cameraColumnWidth =
      Math.max(90, this.viewportSpacer - scrollLeft);
  }

  this.updateTickerTime();
}

onDragEnd() {
  if (!this.isDragging) return;

  this.isDragging = false;
  this.updateTickerTime();

  this.forceSeekNextUpdate = true;
  this.syncStagePlayback();
}

onWheelZoom(event: WheelEvent) {
  this.pinnedPlayhead = null;
  event.preventDefault();

  const viewport =
    this.timelineScroller.nativeElement;

  const rect =
    viewport.getBoundingClientRect();

  const mouseX =
    event.clientX - rect.left;

  const worldX =
    viewport.scrollLeft + mouseX;

  const timeAtCursor =
    this.engine.getTimeFromPixel(worldX);

  // ZOOM
  if (event.deltaY < 0) {
    this.pixelsPerMinute *= 1.1;
  } else {
    this.pixelsPerMinute /= 1.1;
  }

  // clamp
  this.pixelsPerMinute =
    Math.max(1,
      Math.min(this.pixelsPerMinute, 100));

  // rebuild timeline
  this.rebuildTimeline();

  // restore cursor anchor
  const newWorldX =
    this.engine.getPixel(timeAtCursor);

  viewport.scrollLeft =
    newWorldX - mouseX;

    this.updateTickerTime();
}

updateTickerTime() {
  const pinned = this.pinnedPlayhead;
  if (pinned instanceof Date && !Number.isNaN(pinned.getTime())) {
    this.currentTickerTime = pinned;
    const pinnedViewport = this.timelineScroller?.nativeElement;
    if (pinnedViewport && typeof this.engine?.getPixel === 'function') {
      pinnedViewport.scrollLeft = Math.max(
        0,
        this.engine.getPixel(pinned) - this.cursorOffsetInViewport(),
      );
    }
    return;
  }

  const viewport =
    this.timelineScroller.nativeElement;

  if (!this.isPlaying) {
    this.cameraColumnWidth = Math.max(
      90,
      this.viewportSpacer - viewport.scrollLeft
    );
  }

  const worldX =
    viewport.scrollLeft +
    this.getTickerXInsideViewport();

  this.currentTickerTime =
    this.engine.getTimeFromPixel(worldX);
   
}

onPlay() {
  if (this.isPlaying) return;

  this.forceSeekNextUpdate = true;

  this.isPlaying = true;

  this.syncStagePlayback(true);

  requestAnimationFrame(this.animate);
}

onPause() {

  this.isPlaying = false;

  this.syncStagePlayback();

}

private lastPlaybackMap =  new Map<number, string>();



seekToTime(
  time: Date,
  syncFromScroll = true
) {

  this.pinnedPlayhead = null;
  this.currentTickerTime = time;

  const viewport =
    this.timelineScroller.nativeElement;

  const pixel =
    this.engine.getPixel(time);

  viewport.scrollLeft =
    pixel -
    this.getTickerXInsideViewport();

  if (syncFromScroll) {
    this.updateTickerTime();
  }

  // ALWAYS sync playback
  this.syncStagePlayback();

}

skipForward(seconds = 10) {
  const targetTime =
    new Date(this.currentTickerTime.getTime() + seconds * 1000);

  this.seekToTime(targetTime, false);

  this.syncStagePlayback(true); // force URL reload
}

skipBackward(seconds = 10) {
  const targetTime =
    new Date(this.currentTickerTime.getTime() - seconds * 1000);

  this.seekToTime(targetTime, false);

  this.syncStagePlayback(true); // force URL reload
}

animate = () => {

  if (!this.isPlaying) {
    return;
  }

  this.updateTimelineScrollFromTicker();

  requestAnimationFrame(this.animate);

}

onEventSelectionChange($event:any){
  if(this.activeTrimSelection && !$event.value.includes(this.activeTrimSelection.eventType)){
    this.activeTrimSelection = null;
  }
}


/** X of the white cursor inside the scroll area. The camera name shifts it left of center. */
cursorOffsetInViewport(): number {
  return this.getTickerXInsideViewport();
}

private getTickerXInsideViewport(): number {
  const viewport =
    this.timelineScroller.nativeElement;

  const container =
    this.timelineContainerRef.nativeElement;

  const viewportRect =
    viewport.getBoundingClientRect();

  const containerRect =
    container.getBoundingClientRect();

  const tickerXInContainer =
    container.clientWidth / 2;

  return tickerXInContainer -
    (viewportRect.left - containerRect.left);
}

private rebuildEventIndex() {

  const events: TimelineEvent[] = [];

  for (const cam of this.selectedCameraList ?? []) {

    for (const type of this.selectedMotions) {

      const segments =
        cam?.segments?.[type] ?? [];

      for (const seg of segments) {

        events.push({
          type,
          cameraId: cam.id,
          start: new Date(seg.start),
          end: new Date(seg.end),
          segment: seg
        });

      }

    }

  }

  events.sort(
    (a, b) =>
      a.start.getTime() -
      b.start.getTime()
  );

  this.allEvents = events;
}

nextEvent() {

  const active =
    this.getCurrentActiveEvent();

  if (!active) {
    return;
  }

  const currentTime =
    this.currentTickerTime.getTime();

  const next =
    this.allEvents.find(event =>

      event.type !== active.type &&

      event.start.getTime() >
        currentTime

    );

  if (!next) {
    return;
  }

  /**
   * switch frame context
   */
  this.activeEventType =
    next.type;

  this.forceSeekNextUpdate = true;

  this.seekToTime(
    new Date(next.start),
    false
  );

}
previousEvent() {

  const active =
    this.getCurrentActiveEvent();
  if (!active) {
    return;
  }

  const currentTime =
    this.currentTickerTime.getTime();
  const previous =
    [...this.allEvents]
      .reverse()
      .find(event =>

        event.type !== active.type &&

        event.start.getTime() <
          currentTime

      );
  if (!previous) {
    return;
  }

  /**
   * switch frame context
   */
  this.activeEventType =
    previous.type;

  this.forceSeekNextUpdate = true;

  this.seekToTime(
    new Date(previous.start),
    false
  );

}
nextFrame() {

  if (!this.activeEventType) {
    return;
  }

  const currentTime =
    this.currentTickerTime.getTime();

  const next =
    this.allEvents.find(event =>

      event.type ===
        this.activeEventType &&

      event.start.getTime() >
        currentTime

    );

  if (!next) return;

  this.forceSeekNextUpdate = true;

  this.seekToTime(
    new Date(next.start),
    false
  );

}

previousFrame() {
  const active = this.getCurrentActiveEvent();

  if (!active) return;

  const currentTime = this.currentTickerTime.getTime();

  const previous = [...this.allEvents]
    .reverse()
    .find(event =>
      event.type === this.activeEventType &&
      event.start.getTime() < currentTime
    );

  if (!previous) return;

  this.forceSeekNextUpdate = true;

  this.seekToTime(
    new Date(previous.start),
    false
  );
}

private getCurrentActiveEvent(): TimelineEvent | null {

  const current =
    this.currentTickerTime.getTime();

const overlapping =
  this.allEvents.filter(event => {

    return (
      current >= event.start.getTime() &&
      current <= event.end.getTime()
    );

  });

if (!overlapping.length) {
  return null;
}

/**
 * most recently started
 * overlapping event wins
 */
overlapping.sort(
  (a, b) =>
    b.start.getTime() -
    a.start.getTime()
);

return overlapping[0];
}



get selectedMotionOptions(): Motion[] {

  const options =  this.motionOptions.filter(
    motion =>
      this.selectedMotions.includes(
        motion.value
      )
  );
  return options;

}

getSegmentStyles(
  seg: TimelineSegment,
  color: string
) {

  return {
    ...this.engine.getSegmentStyle(seg),
    backgroundColor: color
  };

}

onTrimClick(){
  this.trimMode = true;

}

onTrimClose(){
  this.trimMode = false;
}

onSegmentClick(event: MouseEvent, segment: TimelineSegment, camera:any, eventType: string) {
  if (
      this.trimMode &&
      (event.ctrlKey || event.metaKey)
  ) {

      event.preventDefault();
      event.stopPropagation();
      this.activeEventType =
        eventType;

      this.startTrimSelection(camera,segment, eventType);
      return;
  }
}




  startTrimSelection(camera:any,segment:any, eventType: string){
    this.activeTrimSelection = {
      camera: camera,
      segment,
      trimStart: new Date(segment.start),
      trimEnd: new Date(segment.end),
      eventType: eventType,
      draggingLeft: false,
      draggingRight: false
  };
}

onDownloadSegmentClick(){
    if (!this.activeTrimSelection) {
    return;
  }

    const payload =
    structuredClone(
      this.activeTrimSelection
    );

  this.onDownloadSegment.emit(
    payload
  );
}

onTrimHandleDown(
    event: MouseEvent,
    side: 'left' | 'right'
) {

    event.preventDefault();
    event.stopPropagation();

    if (!this.activeTrimSelection) {
        return;
    }

    this.activeTrimSelection.draggingLeft =
        side === 'left';

    this.activeTrimSelection.draggingRight =
        side === 'right';
}

onTrimDragMove = (event: MouseEvent) => {

    if (!this.activeTrimSelection) {
        return;
    }

    const {
        draggingLeft,
        draggingRight,
        segment
    } = this.activeTrimSelection;

    if (!draggingLeft && !draggingRight) {
        return;
    }

    const viewport =
        this.timelineScroller.nativeElement;

    const rect =
        viewport.getBoundingClientRect();

    const mouseX =
        event.clientX - rect.left;

    const worldX =
        viewport.scrollLeft + mouseX;

    // clamp inside segment
    const clampedPixel =
        this.engine.clampPixelToSegment(
            worldX,
            segment
        );

    const date =
        this.engine.getTimeFromPixel(
            clampedPixel
        );

    // LEFT HANDLE
    if (draggingLeft) {

        if (
            date.getTime() <
            this.activeTrimSelection.trimEnd.getTime()
        ) {

            this.activeTrimSelection.trimStart =
                date;
        }
    }

    // RIGHT HANDLE
    if (draggingRight) {

        if (
            date.getTime() >
            this.activeTrimSelection.trimStart.getTime()
        ) {

            this.activeTrimSelection.trimEnd =
                date;
        }
    }
}
onTrimDragEnd = () => {

    if (!this.activeTrimSelection) {
        return;
    }

    this.activeTrimSelection.draggingLeft = false;
    this.activeTrimSelection.draggingRight = false;
}






private updateTimelineScrollFromTicker() {

  const viewport =
    this.timelineScroller.nativeElement;

  const pixel =
    this.engine.getPixel(
      this.currentTickerTime
    );

  viewport.scrollLeft =
    pixel -
    this.getTickerXInsideViewport();

}

private syncStagePlayback(
  forceReload = false
) {

  const currentTime =
    this.currentTickerTime.getTime();

  for (const cam of this.selectedCameraList ?? []) {

    let matchedSegment: any = null;

    for (const type of this.selectedMotions) {

      const segments =
        cam?.segments?.[type] ?? [];

      const match =
        segments.find((seg: any) => {

          const start =
            new Date(seg.start).getTime();

          const end =
            new Date(seg.end).getTime();

          return (
            currentTime >= start &&
            currentTime <= end
          );

        });

      if (match) {
        matchedSegment = match;
        break;
      }

    }

    if (!matchedSegment) {
      continue;
    }








    this.stage.seek(
      cam.id,
      matchedSegment,
      this.currentTickerTime,
      this.isPlaying,
      this.selectedSpeed,
      forceReload
    );

  }

}
onSpeedChange(speed: number) {

  this.selectedSpeed = speed;

  this.stage.setPlaybackRate(speed);

}



}
